import { PrototypeFirmwareApi } from '@/../generated-client/apis/PrototypeFirmwareApi';
import { useApiStore } from '@/stores/apiStore';
import { ResponseError } from '../../generated-client/runtime';

let prototypeFirmwareApi: PrototypeFirmwareApi | null = null;
let lastConfig: unknown = null;

function getApi(): PrototypeFirmwareApi {
  const { apiConfig } = useApiStore.getState();
  if (prototypeFirmwareApi === null || lastConfig !== apiConfig) {
    prototypeFirmwareApi = new PrototypeFirmwareApi(apiConfig);
    lastConfig = apiConfig;
  }
  return prototypeFirmwareApi;
}

export const getAllPrototypeFirmwares = async () =>
  getApi().apiPrototypeFirmwareGetAllGet();

export const getAllPrototypers = async () =>
  getApi().apiPrototypeFirmwareGetAllPrototypersGet();

export const addPrototypeFirmware = async (
  file: File,
  name: string,
  version: string,
  description: string,
) =>
  getApi().apiPrototypeFirmwareAddPost({
    file,
    name,
    version,
    description,
  });

export const updatePrototypeFirmware = async (
  firmware_id: string,
  name: string,
  version: string,
  description: string,
) =>
  getApi().apiPrototypeFirmwareUpdatePut({
    prototypeFirmwareUpdateModel: { firmware_id, name, version, description },
  });

export const deletePrototypeFirmware = async (firmware_id: string) =>
  getApi().apiPrototypeFirmwareDeleteDelete({
    prototypeFirmwareDeleteModel: { firmware_id },
  });

export const assignUserToFirmware = async (
  firmware_id: string,
  auth_id: string,
) =>
  getApi().apiPrototypeFirmwareAssignUserPut({
    prototypeFirmwareUserModel: { firmware_id, auth_id },
  });

export const removeUserFromFirmware = async (
  firmware_id: string,
  auth_id: string,
) =>
  getApi().apiPrototypeFirmwareRemoveUserPut({
    prototypeFirmwareUserModel: { firmware_id, auth_id },
  });

export const enablePrototyper = async (auth_id: string) =>
  getApi().apiPrototypeFirmwareEnablePrototyperPut({
    prototyperToggleModel: { auth_id },
  });

export const disablePrototyper = async (auth_id: string) =>
  getApi().apiPrototypeFirmwareDisablePrototyperPut({
    prototyperToggleModel: { auth_id },
  });

/**
 * Map common backend error responses to a user-facing string per spec §5.1.
 * The caller should pass the returned string to `showToast(..., ToastType.DANGER)`
 * and — for 404 / 409 — trigger a refetch.
 */
export const formatPrototypeFirmwareError = (
  error: unknown,
  resourceLabel: string = 'Resource',
): { message: string; shouldRefetch: boolean } => {
  if (error instanceof ResponseError) {
    const status = error.response.status;
    if (status === 401) {
      return {
        message: "You don't have permission to perform this action.",
        shouldRefetch: false,
      };
    }
    if (status === 400) {
      return {
        message: 'Invalid input. Please check the form and try again.',
        shouldRefetch: false,
      };
    }
    if (status === 404) {
      return {
        message: `${resourceLabel} no longer exists. Refreshing the list.`,
        shouldRefetch: true,
      };
    }
    if (status === 409) {
      return {
        message: 'This firmware was updated by someone else. Refreshing.',
        shouldRefetch: true,
      };
    }
    if (status === 429) {
      return {
        message: 'Server is busy. Please try again in a moment.',
        shouldRefetch: false,
      };
    }
  }
  return {
    message: 'Something went wrong. Please try again.',
    shouldRefetch: false,
  };
};
